import type {
  OptionsDocument,
  PresentationBlock,
  PresentationOptionV2,
  PresentationSectionV2,
  QuoteFile
} from 'paintscout'
import {
  buildPresentationSectionFromOption,
  createPresentationNextBlock,
  createPresentationNextSection,
  getDefaultPresentationImages,
  getPresentationBlockOptions,
  getPresentationNextSectionOptions,
  replaceSectionImages
} from '@paintscout/util/builder'
import {
  PRESENTATION_BLOCK_DRAG_MIME_TYPE,
  type PresentationBlockDragPayload
} from '../PresentationEditSidebar/AddPanel/blockDragPayload'
import {
  PRESENTATION_SECTION_DRAG_MIME_TYPE,
  type PresentationSectionDragPayload
} from '../PresentationEditSidebar/AddPanel/sectionDragPayload'

/**
 * Reads/builds what the canvas zone rails (#22208) need out of a native HTML5 DataTransfer dropped
 * from the Add panel. Kept out of dragBlocks.ts deliberately: that module only ever translates an
 * existing node's move (react-beautiful-dnd reorder), while this is a *new*-node insertion, sourced
 * from a different drag system (native DnD — see blockDragPayload.ts's module doc for why the two
 * coexist).
 */

export function readBlockDragPayload(dataTransfer: DataTransfer): PresentationBlockDragPayload | null {
  const raw = dataTransfer.getData(PRESENTATION_BLOCK_DRAG_MIME_TYPE)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function readSectionDragPayload(dataTransfer: DataTransfer): PresentationSectionDragPayload | null {
  const raw = dataTransfer.getData(PRESENTATION_SECTION_DRAG_MIME_TYPE)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * Builds the block to insert from a dropped payload, or `null` when the drop shouldn't be honored:
 * a malformed `type` (not a non-empty string), or an unknown type (stale catalog snapshot).
 */
export function buildDroppedBlock(args: {
  options?: OptionsDocument
  presentation: PresentationOptionV2
  payload: PresentationBlockDragPayload
}): PresentationBlock | null {
  const { options, presentation, payload } = args

  // `payload` is parsed from attacker-controlled DataTransfer content — nothing guarantees `type` is
  // even a string before it reaches the catalog lookup below.
  if (typeof payload?.type !== 'string' || !payload.type) {
    return null
  }

  const blockOption = getPresentationBlockOptions({ options }).find((option) => option.type === payload.type)
  if (!blockOption) {
    return null
  }

  const newBlock = createPresentationNextBlock({ blockOption })
  const images = getDefaultPresentationImages({ options, presentation })
  const { section: blockWithImages } = replaceSectionImages({ section: newBlock, images })

  return blockWithImages
}

/**
 * Builds the section to insert from a dropped payload, or `null` when the optionKey doesn't resolve
 * — a stale catalog snapshot, or a feature-gated catalog entry (e.g. Sherwin-Williams) that dropped
 * out of `getPresentationNextSectionOptions` since the tile was rendered. `'empty'` is the blank-
 * section tile, which has no catalog entry by design (mirrors `EmptySectionTile`).
 *
 * `images` is the photo list the dragged tile previewed with (`catalogDrag.images`), so the section
 * that lands carries the same photos in the same order as the tile. Pass `[]` and the catalog's grey
 * placeholders survive, which is what an account with no `advanced.defaultImages` gets.
 */
export function buildDroppedSection(args: {
  options?: OptionsDocument
  payload: PresentationSectionDragPayload
  images?: QuoteFile[]
}): PresentationSectionV2 | null {
  const { options, payload, images } = args

  if (typeof payload?.optionKey !== 'string' || !payload.optionKey) {
    return null
  }

  if (payload.optionKey === 'empty') {
    return createPresentationNextSection()
  }

  const sectionOption = getPresentationNextSectionOptions({ options }).find(
    (option) => option.optionKey === payload.optionKey
  )
  if (!sectionOption) {
    return null
  }

  return buildPresentationSectionFromOption({ sectionOption, images })
}

/**
 * Whether a zone rail of a given kind should light up for the drag currently in progress.
 *
 * The design mock's own `zoneAllows` (`Presentation Builder V2.dc.html` L2665-2666) is looser — it
 * also lights up ordinary block zones during a section drag AND lights up section zones during a
 * block drag (so a block dropped at a section boundary lands in whichever section is adjacent).
 * This is deliberately stricter: a section-level zone (between sections, or at the top/bottom of a
 * page) only accepts a section drag, and an in-section block zone only accepts a block drag. That
 * matches the *existing* click affordances one-for-one — `SectionDividerMenu`'s "+" only ever
 * creates a section, `AddBlockButton`'s "+" only ever creates a block — so drag-to-insert doesn't
 * introduce a new "drop a block right on a section boundary" behavior that has no click equivalent.
 */
export function catalogZoneAllows(kind: 'block' | 'section', zoneAccepts: 'block' | 'section'): boolean {
  return kind === zoneAccepts
}

export interface CanvasDropZoneBand {
  id: string
  accepts: 'block' | 'section'
  /** Viewport coordinates, as `getBoundingClientRect` reports them. */
  top: number
  bottom: number
}

/**
 * Picks the zone rail a drop at `clientY` should land in — the nearest rail that accepts `kind`,
 * measured vertically only.
 *
 * The canvas owns hit-testing rather than each rail owning its own, because each rail is only ~44px
 * tall and only exists mid-drag. Releasing a few pixels off one used to be a silent no-op: the
 * canvas root never called `preventDefault()` on dragover, so the browser rejected the drop outright
 * and the user got a `not-allowed` cursor with no explanation (#22688). Now every release inside the
 * canvas lands somewhere, and the picked rail highlights while the drag is still in progress.
 *
 * A rail the pointer is inside scores 0. Otherwise it scores the distance to its nearer edge. The
 * first rail at the winning score wins, so an exact tie resolves upward in document order.
 * `null` means no rail accepts this kind — the drop is not honored.
 */
export function pickNearestDropZone(args: {
  zones: CanvasDropZoneBand[]
  kind: 'block' | 'section'
  clientY: number
}): string | null {
  const { zones, kind, clientY } = args

  let bestId: string | null = null
  let bestDistance = Number.POSITIVE_INFINITY

  zones.forEach((zone) => {
    if (!catalogZoneAllows(kind, zone.accepts)) {
      return
    }

    const distance =
      clientY >= zone.top && clientY <= zone.bottom
        ? 0
        : Math.min(Math.abs(clientY - zone.top), Math.abs(clientY - zone.bottom))

    if (distance < bestDistance) {
      bestDistance = distance
      bestId = zone.id
    }
  })

  return bestId
}
